import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Zap, ArrowRight, Sparkles, Leaf, Sun, BatteryCharging, AlertCircle } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const from = location.state?.from?.pathname || '/';

  const parseLoginError = (err) => {
    // Nessuna response: problema di rete / CORS / server irraggiungibile
    if (!err?.response) {
      if (err?.code === 'ECONNABORTED') return 'Il server non risponde (timeout). Riprova tra qualche secondo.';
      return 'Impossibile contattare il server. Verifica la connessione internet e riprova.';
    }
    const status = err.response.status;
    const detail = err.response.data?.detail;
    if (status === 401) return detail || 'Email o password non validi. Controlla le credenziali e riprova.';
    if (status === 403) return detail || 'Il tuo account non è ancora attivo o è stato disabilitato. Contatta l\'amministratore.';
    if (status === 429) return 'Troppi tentativi di accesso. Attendi qualche minuto e riprova.';
    if (status >= 500) return 'Errore del server. Riprova tra qualche minuto o contatta il supporto.';
    return detail || `Errore imprevisto (${status}). Riprova.`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!email.trim() || !password) {
      setErrorMsg('Inserisci email e password.');
      return;
    }
    setSubmitting(true);
    try {
      const u = await login(email.trim(), password, rememberMe);
      toast.success(`Benvenuto, ${u.full_name || u.email}`);
      // Redirect specifico per ruolo (se non c'è un `from` esplicito)
      let target = from;
      if (from === '/' && u?.role === 'installer') target = '/installer/interventi';
      navigate(target, { replace: true });
    } catch (err) {
      const msg = parseLoginError(err);
      setErrorMsg(msg);
      toast.error(msg);
      // Focus password per rapida ripetizione
      const pwd = document.getElementById('password');
      if (pwd) pwd.focus();
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative flex-col justify-between p-10 text-white overflow-hidden bg-[#0A0E0B]">
        <div className="absolute inset-0 opacity-90 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(159,255,92,0.16),transparent_55%),radial-gradient(900px_circle_at_80%_30%,rgba(20,184,166,0.10),transparent_50%)]" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,14,11,0) 30%, rgba(10,14,11,0.65) 100%)' }} />
        <div className="relative z-10">
          <div className="flex items-center gap-2">
            <div className="size-9 rounded-xl grid place-items-center" style={{ background: '#9FFF5C', color: '#0A0E0B' }}><Zap className="size-5" /></div>
            <div>
              <div className="font-heading text-lg font-semibold">CRM Utility Boost</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 space-y-4 max-w-md">
          <h1 className="font-heading text-4xl font-semibold leading-tight">Per un mondo più <span style={{ color: '#9FFF5C' }}>verde</span> e più <span style={{ color: '#9FFF5C' }}>pulito</span>.</h1>
          <p className="text-white/70 text-sm leading-relaxed">Ogni impianto installato è un passo verso un futuro a zero emissioni. Trasformiamo case e imprese in protagoniste della transizione energetica.</p>
          <div className="grid grid-cols-2 gap-3 pt-4">
            <Feature icon={Sun} text="Fotovoltaico & accumulo" />
            <Feature icon={Leaf} text="Pompe di calore" />
            <Feature icon={BatteryCharging} text="Wallbox & ricarica EV" />
            <Feature icon={Sparkles} text="Solare termico & caldaie" />
          </div>
        </div>
        <div className="relative z-10 text-xs text-white/50">© {new Date().getFullYear()} Scintilla Hub - Smart Utility Agency</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <div className="size-9 rounded-xl bg-primary text-primary-foreground grid place-items-center"><Zap className="size-5" /></div>
            <div>
              <div className="font-heading text-lg font-semibold">CRM Utility Boost</div>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-semibold mb-2">Accedi</h2>
          <p className="text-sm text-muted-foreground mb-6">Insieme rendiamo il pianeta più verde, una casa alla volta.</p>

          <Card>
            <CardContent className="p-5">
              {errorMsg && (
                <Alert variant="destructive" className="mb-4" data-testid="login-error-alert">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Accesso non riuscito</AlertTitle>
                  <AlertDescription>{errorMsg}</AlertDescription>
                </Alert>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); if (errorMsg) setErrorMsg(''); }} required autoComplete="username" data-testid="login-email-input" />
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="login-forgot-link">Password dimenticata?</Link>
                  </div>
                  <Input id="password" type="password" value={password} onChange={(e) => { setPassword(e.target.value); if (errorMsg) setErrorMsg(''); }} required autoComplete="current-password" data-testid="login-password-input" />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox
                    id="remember-me"
                    checked={rememberMe}
                    onCheckedChange={(v) => setRememberMe(!!v)}
                    data-testid="login-remember-me-checkbox"
                  />
                  <Label htmlFor="remember-me" className="text-sm font-normal cursor-pointer select-none">
                    Mantieni l&apos;accesso su questo dispositivo (30 giorni)
                  </Label>
                </div>
                <Button type="submit" className="w-full" disabled={submitting} data-testid="login-submit-button">
                  {submitting ? 'Accesso...' : 'Entra'} <ArrowRight className="size-4 ml-1" />
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="text-sm text-muted-foreground mt-6 text-center">
            Non hai un account? <Link to="/register" className="text-foreground font-medium hover:underline" data-testid="login-register-link">Registrati</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const Feature = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 text-sm text-white/80">
    <div className="size-8 rounded-lg grid place-items-center" style={{ background: 'rgba(159,255,92,0.12)', color: '#9FFF5C' }}><Icon className="size-4" /></div>
    <span>{text}</span>
  </div>
);
